/**
 * Hawaii State-Specific Diagnostic Questions
 * REBUILT with CORRECT regulatory citations
 * 
 * CRITICAL DISTINCTION - Hawaii has TWO separate license types:
 * 
 * 1. HOME HEALTH AGENCY (Skilled Services)
 *    - Regulation: HAR 11-97.1 (replaced HAR 11-97 effective 2022)
 *    - Authority: DOH-OHCA Medicare Section
 *    - Services: Skilled nursing, therapy under physician direction
 *    - Requires: Certificate of Need (CON) from SHPDA for Medicare participation
 *    - Federal: 42 CFR 484 for Medicare-certified agencies
 * 
 * 2. HOME CARE AGENCY (Non-Medical Services)
 *    - Regulation: HAR 11-700 (effective August 1, 2018)
 *    - Authority: DOH-OHCA State Licensing Section
 *    - Services: Personal care, homemaker, respite (non-skilled)
 *    - Requires: State license only, NO CON required
 *    - Statute: HRS 321-14.8
 * 
 * COMMON REQUIREMENTS (Both Agency Types):
 *    - Background checks: HAR 11-106, HRS 321-15.2 (via Fieldprint)
 *    - TB clearance: HAR 11-164.2 (ANNUAL requirement)
 *    - Abuse reporting: HRS 346-224 (mandatory reporters)
 *    - Licensing fees: HAR 11-103.1
 * 
 * PRIMARY ERROR IN PREVIOUS FILE: Cited "HAR 11-100" throughout
 * HAR 11-100 = Adult Residential Care Homes (ARCH) - WRONG regulation
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const HAWAII_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: AGENCY TYPE & LICENSING
  // ============================================
  {
    id: 'HI-A1',
    itemId: 'A1',
    stateCode: 'HI',
    title: 'Agency Type Determination',
    description: 'Hawaii requires different licenses for skilled vs non-medical home care services',
    severity: 'high',
    regulationCode: '11-97.1',
    regulationName: 'Agency Type',
    agencyName: 'Hawaii Department of Health - Office of Health Care Assurance',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'What type of home care services does your agency provide?',
        inputType: 'select',
        options: [
          { value: 'hha', label: 'Home Health Agency - Skilled nursing/therapy under physician direction (HAR 11-97.1)' },
          { value: 'hca', label: 'Home Care Agency - Personal care/homemaker services only (HAR 11-700)' },
          { value: 'both', label: 'Both skilled and non-medical services (requires both licenses)' },
          { value: 'unsure', label: 'Not sure which category applies' },
        ],
        required: true,
        helpText: 'Home Health = skilled nursing under physician orders. Home Care = personal care/homemaker (non-medical).',
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['hha', 'hca', 'both'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'unsure', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Your agency type has been identified. Ensure you hold the appropriate license(s) for your service type.',
      atRisk: 'You must determine whether you are a Home Health Agency (skilled services, HAR 11-97.1) or Home Care Agency (non-medical, HAR 11-700). Contact DOH-OHCA at (808) 586-4400 for guidance.',
      nonCompliant: 'Operating without proper classification creates significant compliance risk.',
      unknown: 'Contact DOH-OHCA at (808) 586-4400 to determine your agency classification.',
    },
    stateNotes: [
      'Hawaii has TWO distinct license types for in-home services',
      'Home Health Agency (HAR 11-97.1): Skilled nursing, therapy, requires physician direction',
      'Home Care Agency (HAR 11-700): Personal care, homemaker - no skilled services',
      'Providing skilled services under a Home Care license is ILLEGAL',
      'Previous file incorrectly cited HAR 11-100 (Adult Residential Care Homes)',
    ],
  },

  {
    id: 'HI-A2',
    itemId: 'A2',
    stateCode: 'HI',
    title: 'Home Health Agency License (HAR 11-97.1)',
    description: 'Skilled home health agencies must be licensed under HAR 11-97.1',
    severity: 'high',
    regulationCode: '11-97.1',
    regulationName: 'HHA License',
    agencyName: 'DOH-OHCA Medicare Section',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/home/medicare-section/opening-a-home-health-agency/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current Hawaii Home Health Agency license from DOH?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, current and active' },
          { value: 'pending', label: 'Application pending' },
          { value: 'expired', label: 'License expired' },
          { value: 'none', label: 'No license / Not applicable (Home Care Agency only)' },
        ],
        required: true,
        helpText: 'Home Health Agency licenses are valid for 2 years per HAR 11-97.1-3',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'current',
        },
      },
      {
        id: 'q3',
        question: 'Did you obtain a Certificate of Need (CON) from SHPDA?',
        inputType: 'select',
        options: [
          { value: 'approved', label: 'Yes, CON approved' },
          { value: 'not_needed', label: 'SHPDA issued letter stating CON not needed (non-Medicare)' },
          { value: 'pending', label: 'CON application pending' },
          { value: 'none', label: 'No CON obtained' },
        ],
        helpText: 'CON required for Medicare/Medicaid participation. Non-Medicare agencies need SHPDA exemption letter.',
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'none',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'current' },
        { condition: 'includes', questionId: 'q3', value: ['approved', 'not_needed'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['expired'], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'none', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Home Health Agency license status is in order per HAR 11-97.1.',
      atRisk: 'Your HHA license application is pending or requires renewal. You cannot provide skilled services until licensed.',
      nonCompliant: 'Operating a Home Health Agency in Hawaii without a valid DOH license violates HAR 11-97.1-3. This carries fines up to $500 and/or imprisonment up to one year per HAR 11-97.1-7. Contact DOH-OHCA immediately.',
      unknown: 'Verify your Home Health Agency license status with DOH-OHCA at (808) 586-4400.',
    },
    stateNotes: [
      'Home Health Agency license valid for 2 years (HAR 11-97.1-3)',
      'Certificate of Need (CON) required from SHPDA for Medicare/Medicaid participation',
      'SHPDA: (808) 587-0788 | https://health.hawaii.gov/shpda/',
      'Non-Medicare agencies need SHPDA exemption letter',
      'License must be posted in conspicuous place (HAR 11-97.1-3)',
      'Notify patients 30 days before license surrender/revocation',
    ],
  },

  {
    id: 'HI-A3',
    itemId: 'A3',
    stateCode: 'HI',
    title: 'Home Care Agency License (HAR 11-700)',
    description: 'Non-medical home care agencies must be licensed under HAR 11-700',
    severity: 'high',
    regulationCode: '11-700',
    regulationName: 'HCA License',
    agencyName: 'DOH-OHCA State Licensing Section',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/state-licensing-section/hcal/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current Hawaii Home Care Agency license from DOH?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, current and active' },
          { value: 'pending', label: 'Application pending' },
          { value: 'expired', label: 'License expired' },
          { value: 'none', label: 'No license / Not applicable (Home Health Agency only)' },
        ],
        required: true,
        helpText: 'Home Care Agency licenses are valid for 2 years per HAR 11-700-4',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'current',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'current', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'expired', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Home Care Agency license status is in order per HAR 11-700.',
      atRisk: 'Your HCA license application is pending. New agencies must be licensed BEFORE providing services per HAR 11-700-4.',
      nonCompliant: 'Operating a Home Care Agency without a valid DOH license violates HRS 321-14.8 and HAR 11-700-4. Fines up to $500 per HAR 11-700-10. Contact DOH-OHCA immediately.',
      unknown: 'Verify your Home Care Agency license status with DOH-OHCA at (808) 586-4400.',
    },
    stateNotes: [
      'Home Care Agency license valid for 2 years (HAR 11-700-4)',
      'No Certificate of Need required for Home Care Agencies',
      'Apply at least 30 days before license expiration for renewal',
      'License fee: $500 non-refundable application fee (HAR 11-103.1)',
      'DOH may take enforcement action if not licensed within 3 months of starting operations',
      'Exempt: Nonprofit entities with 90%+ volunteers providing only homemaker services',
    ],
  },

  {
    id: 'HI-A4',
    itemId: 'A4',
    stateCode: 'HI',
    title: 'Business Entity Registration (DCCA)',
    description: 'Hawaii requires valid business registration with DCCA',
    severity: 'medium',
    regulationCode: '414',
    regulationName: 'Business Registration',
    agencyName: 'Department of Commerce and Consumer Affairs - Business Registration Division',
    agencyAbbr: 'DCCA-BREG',
    agencyPhone: '(808) 586-2727',
    agencyUrl: 'https://cca.hawaii.gov/breg/',
    questions: [
      {
        id: 'q1',
        question: 'Is your business entity registration with DCCA current and in good standing?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, current and in good standing' },
          { value: 'pending', label: 'Annual report pending' },
          { value: 'delinquent', label: 'Delinquent or not in good standing' },
          { value: 'unsure', label: 'Not sure of status' },
        ],
        required: true,
        helpText: 'Annual reports must be filed with DCCA to maintain good standing',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'current', result: 'COMPLIANT' },
      { condition: 'equals', value: 'pending', result: 'AT_RISK' },
      { condition: 'includes', value: ['delinquent', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your business registration is current with DCCA.',
      atRisk: 'Your annual report is due. File with DCCA to maintain good standing.',
      nonCompliant: 'Your business registration is not in good standing. Contact DCCA at (808) 586-2727. Good standing required to maintain DOH license.',
      unknown: 'Verify your business registration status at hbe.ehawaii.gov.',
    },
    stateNotes: [
      'Annual reports due each year by end of anniversary quarter',
      'Online filing available at hbe.ehawaii.gov',
      'Good standing required to maintain DOH license',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND CHECKS (HAR 11-106)
  // ============================================
  {
    id: 'HI-B1',
    itemId: 'B1',
    stateCode: 'HI',
    title: 'Criminal Background Checks (HAR 11-106)',
    description: 'Hawaii requires comprehensive background checks through Fieldprint for all health care workers',
    severity: 'high',
    regulationCode: '11-106',
    regulationName: 'Background Checks',
    agencyName: 'DOH-OHCA via Fieldprint (designated agent)',
    agencyAbbr: 'Fieldprint',
    agencyPhone: '(877) 614-4364',
    agencyUrl: 'https://fieldprinthawaii.com/',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct background checks through Fieldprint (Hawaii\'s designated agent) for ALL employees with direct patient/client contact?',
        inputType: 'select',
        options: [
          { value: 'all_fieldprint', label: 'Yes, all staff processed through Fieldprint' },
          { value: 'partial', label: 'Some staff, not all' },
          { value: 'other_vendor', label: 'Using a different background check vendor' },
          { value: 'none', label: 'No background checks conducted' },
        ],
        required: true,
        helpText: 'HAR 11-106 requires background checks through DOH\'s designated agent (Fieldprint)',
      },
      {
        id: 'q2',
        question: 'Which registries are checked as part of your background screening? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'state_criminal', label: 'Hawaii state criminal history (HCJDC)' },
          { value: 'fbi', label: 'FBI national criminal history (fingerprint-based)' },
          { value: 'aps', label: 'Adult Protective Services (APS) central registry' },
          { value: 'can', label: 'Child Abuse and Neglect (CAN) registry' },
          { value: 'sex_offender', label: 'Sex offender registry' },
          { value: 'cna_registry', label: 'Certified Nurse Aide registry' },
          { value: 'out_of_state', label: 'Out-of-state registries (if employee previously resided elsewhere)' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'How frequently do you conduct background check renewals?',
        inputType: 'select',
        options: [
          { value: 'annual', label: 'Annually' },
          { value: 'biennial', label: 'Every 2 years (biennial)' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'never', label: 'No renewal process' },
        ],
        required: true,
        helpText: 'HAR 11-106-5 requires annual or biennial background check renewals',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all_fieldprint' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'includes', questionId: 'q3', value: ['annual', 'biennial'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'equals', questionId: 'q3', value: 'hire_only' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'none' },
        { condition: 'equals', questionId: 'q1', value: 'other_vendor' },
        { condition: 'equals', questionId: 'q3', value: 'never' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check process meets Hawaii HAR 11-106 requirements through Fieldprint.',
      atRisk: 'Background check process is incomplete. Ensure ALL direct patient access employees are screened through Fieldprint and renewals are conducted annually or biennially.',
      nonCompliant: 'Background checks do not meet HAR 11-106 requirements. Hawaii REQUIRES use of Fieldprint as the designated agent. Using other vendors does not meet compliance. Failure to comply may result in license revocation.',
      unknown: 'Contact Fieldprint at (877) 614-4364 or visit fieldprinthawaii.com to establish compliant screening process.',
    },
    stateNotes: [
      'Fieldprint is Hawaii\'s ONLY designated background check agent per HAR 11-106',
      'Apply online at www.fieldprinthawaii.com',
      'Background checks include: state/FBI criminal, APS, CAN, sex offender, CNA registries',
      'HRS 321-15.2 defines "background check" and "disqualifying information"',
      'Rap back program: Fingerprints entered for ongoing monitoring',
      'Annual or biennial (every 2 years) renewal required per HAR 11-106-5',
      'Fitness determinations: Green Light = cleared, Yellow/Red = requires review',
    ],
  },

  {
    id: 'HI-B2',
    itemId: 'B2',
    stateCode: 'HI',
    title: 'Background Check Timing and Documentation',
    description: 'Background checks must be completed BEFORE patient/client contact',
    severity: 'high',
    regulationCode: '11-106',
    regulationName: 'Pre-Employment Screening',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/home/how-to-obtain-a-background-check/',
    questions: [
      {
        id: 'q1',
        question: 'Do any staff begin working with patients/clients BEFORE their Fieldprint background check clearance is received?',
        inputType: 'select',
        options: [
          { value: 'never', label: 'Never - all staff wait for clearance' },
          { value: 'rarely', label: 'Rarely - only in urgent situations with supervision' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'often', label: 'Often - we start staff before clearance' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you maintain documented evidence of background check results for all employees?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all results printed and maintained on file' },
          { value: 'most', label: 'Most, but some may be missing' },
          { value: 'electronic', label: 'Electronic records only in Fieldprint system' },
          { value: 'none', label: 'No documentation maintained' },
        ],
        required: true,
        helpText: 'OHCA does not retain background check reports - facilities must keep their own records',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance - this is a DIRECT VIOLATION, not just a risk
      { condition: 'includes', questionId: 'q1', value: ['sometimes', 'often'], result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if no documentation maintained
      { condition: 'equals', questionId: 'q2', value: 'none', result: 'NON_COMPLIANT' },
      // COMPLIANT only if never start before clearance AND proper documentation
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'never' },
        { condition: 'includes', questionId: 'q2', value: ['all', 'electronic'] },
      ], result: 'COMPLIANT' },
      // AT_RISK for rare situations or documentation gaps
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'rarely' },
        { condition: 'equals', questionId: 'q2', value: 'most' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check timing and documentation meets HAR 11-106 requirements.',
      atRisk: 'Some gaps exist in background check timing or documentation. Ensure NO patient/client contact before clearance and maintain documented records.',
      nonCompliant: 'VIOLATION: Allowing staff to work before background check clearance is a direct violation of HAR 11-106. Per regulations, employees working before clearance is prohibited. Immediately remove uncleared staff from patient assignments.',
      unknown: 'Review background check policies against HAR 11-106 requirements.',
    },
    stateNotes: [
      'OHCA does not retain background check reports - YOU must keep copies',
      'Print and maintain all Fieldprint results to demonstrate compliance',
      'NO patient/client contact permitted before "Green Light" fitness determination',
      'Exemption requests go to Fieldprint, appeals go to OHCA',
    ],
  },

  {
    id: 'HI-B3',
    itemId: 'B3',
    stateCode: 'HI',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal exclusion list checks required for Medicare/Medicaid participation',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify ALL employees against the OIG and SAM exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, at hire AND monthly thereafter' },
          { value: 'hire', label: 'Yes, at hire only' },
          { value: 'no', label: 'No / not consistently verified' },
        ],
        required: true,
        helpText: 'CMS requires monthly exclusion list checks for Medicare/Medicaid providers',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion list verification conducted at hire and monthly.',
      atRisk: 'Exclusion list verification at hire only is insufficient. Implement monthly checks.',
      nonCompliant: 'No OIG/SAM exclusion list verification conducted. This creates significant Medicare/Medicaid compliance risk.',
      unknown: 'Implement OIG/SAM exclusion list verification process.',
    },
    stateNotes: [
      'OIG LEIE: exclusions.oig.hhs.gov',
      'SAM: sam.gov',
      'Monthly verification required for Medicare/Medicaid participation',
      'Employing excluded individuals = Civil Monetary Penalties',
    ],
  },

  // ============================================
  // SECTION C: TB CLEARANCE (HAR 11-164.2)
  // ============================================
  {
    id: 'HI-C1',
    itemId: 'C1',
    stateCode: 'HI',
    title: 'TB Clearance - ANNUAL Requirement (HAR 11-164.2)',
    description: 'Hawaii requires ANNUAL TB clearance for all health care workers - more stringent than CDC recommendations',
    severity: 'high',
    regulationCode: '11-164.2-24',
    regulationName: 'TB Clearance',
    agencyName: 'Hawaii Department of Health - TB Control Branch',
    agencyAbbr: 'DOH-TB',
    agencyPhone: '(808) 832-5731',
    agencyUrl: 'https://health.hawaii.gov/tb/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff with patient/client contact have current TB clearance on file (within past 12 months)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB clearance (within 12 months)' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'initial_only', label: 'Initial clearance only, no annual renewals' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'Hawaii requires ANNUAL TB clearance per HAR 11-164.2 - this is MORE stringent than CDC recommendations due to Hawaii\'s higher TB rates',
      },
      {
        id: 'q2',
        question: 'Are new employees obtaining TB clearance BEFORE first patient/client contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always before contact' },
          { value: 'no', label: 'No / sometimes start before clearance' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q1', value: 'initial_only' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB clearance documentation meets Hawaii HAR 11-164.2 ANNUAL requirement.',
      atRisk: 'Some TB clearances may be expired or tracking is inconsistent. Hawaii requires ANNUAL TB clearance - not every 2 years like some states.',
      nonCompliant: 'Staff are working without valid TB clearance. This violates HAR 11-164.2. Obtain TB testing immediately through DOH TB Control Branch.',
      unknown: 'Contact DOH TB Control Branch at (808) 832-5731 for TB clearance requirements.',
    },
    stateNotes: [
      'Hawaii requires ANNUAL TB clearance per HAR 11-164.2-24',
      'More stringent than CDC due to Hawaii\'s higher TB conversion rates',
      'TB clearance required within 12 months PRIOR to employment/contact',
      'TB Control Branch: (808) 832-5731',
      'Forms and manual: health.hawaii.gov/tb/forms-2/',
      'Lanakila Health Center TB Clinic: 1700 Lanakila Ave, Honolulu',
      'Positive TB tests require chest X-ray and medical clearance',
    ],
  },

  // ============================================
  // SECTION D: MANDATORY REPORTING (HRS 346-224)
  // ============================================
  {
    id: 'HI-D1',
    itemId: 'D1',
    stateCode: 'HI',
    title: 'Mandatory Reporter Training (HRS 346-224)',
    description: 'Home care workers are mandatory reporters of vulnerable adult abuse in Hawaii',
    severity: 'high',
    regulationCode: '346-224',
    regulationName: 'Mandatory Reporting',
    agencyName: 'Hawaii Department of Human Services - Adult Protective Services',
    agencyAbbr: 'DHS-APS',
    agencyPhone: '(808) 832-5115',
    agencyUrl: 'https://humanservices.hawaii.gov/ssd/home/adult-services/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers receive training on mandatory reporter responsibilities under HRS 346-224?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff trained during orientation and periodically' },
          { value: 'most', label: 'Most staff are trained' },
          { value: 'some', label: 'Some staff trained' },
          { value: 'no', label: 'No training provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know the centralized APS reporting hotline number?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, (808) 832-5115 is posted and trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Does your training cover these types of reportable abuse? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physical', label: 'Physical abuse' },
          { value: 'neglect', label: 'Neglect or self-neglect' },
          { value: 'financial', label: 'Financial exploitation' },
          { value: 'psychological', label: 'Psychological/emotional abuse' },
          { value: 'sexual', label: 'Sexual abuse' },
          { value: 'caregiver_neglect', label: 'Caregiver neglect' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q3', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on mandatory reporter duties per HRS 346-224.',
      atRisk: 'Mandatory reporter training is inconsistent. ALL direct care workers are mandatory reporters under Hawaii law - ensure comprehensive training.',
      nonCompliant: 'Failure to train staff on mandatory reporter duties violates HRS 346-224. Staff who knowingly fail to report suspected abuse face petty misdemeanor charges (up to 30 days imprisonment and/or fine).',
      unknown: 'Contact DHS-APS at (808) 832-5115 for mandatory reporter training resources.',
    },
    stateNotes: [
      'HRS 346-224 specifically lists "home care workers" as mandatory reporters',
      'Centralized APS Hotline: (808) 832-5115 (statewide)',
      'Report orally FIRST, then follow up with written report (Form DHS 1640)',
      'Failure to report = petty misdemeanor (up to 30 days and/or fine)',
      'Good faith reporters have immunity from liability',
      'Abuse types: physical, psychological, sexual, financial exploitation, neglect, self-neglect',
      '"Vulnerable adult" = 18+ who cannot protect self due to mental/developmental/physical impairment',
    ],
  },

  {
    id: 'HI-D2',
    itemId: 'D2',
    stateCode: 'HI',
    title: 'Incident Reporting Procedures',
    description: 'Written procedures for reporting incidents and suspected abuse',
    severity: 'high',
    regulationCode: '346-224',
    regulationName: 'Incident Reporting',
    agencyName: 'DHS-APS',
    agencyAbbr: 'DHS-APS',
    agencyPhone: '(808) 832-5115',
    agencyUrl: 'https://humanservices.hawaii.gov/ssd/home/adult-services/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written incident and abuse reporting policy?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written policy with APS hotline information' },
          { value: 'informal', label: 'Informal process, not fully documented' },
          { value: 'no', label: 'No incident reporting policy' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the APS reporting form (DHS 1640) readily available to staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'informal', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident and abuse reporting procedures are documented with APS resources available.',
      atRisk: 'Incident reporting process is informal. Document procedures and ensure APS hotline and Form DHS 1640 are readily available.',
      nonCompliant: 'No incident reporting policy in place. This violates HRS 346-224. Immediately develop written procedures.',
      unknown: 'Review incident reporting requirements under HRS 346-224.',
    },
    stateNotes: [
      'Form DHS 1640: humanservices.hawaii.gov/ssd/files/2015/04/DHS-1640-Rev.-3-15-Form-Fill.pdf',
      'Oral report FIRST to (808) 832-5115, then written report',
      'Fax completed form to APS immediately after verbal report',
    ],
  },

  // ============================================
  // SECTION E: PERSONNEL & TRAINING
  // ============================================
  {
    id: 'HI-E1',
    itemId: 'E1',
    stateCode: 'HI',
    title: 'Personnel File Requirements',
    description: 'Required documentation in employee personnel files',
    severity: 'high',
    regulationCode: '11-700-9',
    regulationName: 'Personnel Files',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'license', label: 'Professional license verification (DCCA PVL) if applicable' },
          { value: 'background', label: 'Fieldprint background check clearance (Green Light)' },
          { value: 'tb', label: 'TB clearance (current within 12 months)' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'job_desc', label: 'Written job description signed by employee' },
          { value: 'orientation', label: 'Orientation documentation' },
          { value: 'training', label: 'Training and competency records' },
          { value: 'hipaa', label: 'HIPAA/confidentiality agreement' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required Hawaii documentation.',
      atRisk: 'Personnel files are missing some required documents. Ensure ALL files include: Fieldprint clearance, TB clearance, training records, job descriptions.',
      nonCompliant: 'Personnel files are significantly incomplete. DOH surveyors will cite incomplete files. Immediately audit and complete all personnel files.',
      unknown: 'Audit personnel files against HAR requirements.',
    },
    stateNotes: [
      'HAR 11-700-9(d) specifies personnel documentation requirements for Home Care Agencies',
      'HAR 11-97.1-5 specifies personnel requirements for Home Health Agencies',
      'Written job descriptions required per HAR 11-700-9(d)(1)',
      'Written HR policies must be given to each staff member per HAR 11-700-9(d)(2)',
      'TB clearance, background check, and training records MUST be current',
    ],
  },

  {
    id: 'HI-E2',
    itemId: 'E2',
    stateCode: 'HI',
    title: 'Staff Qualifications - Personal Care Aides (HAR 11-700)',
    description: 'Home Care Agency personal care aides must meet qualification requirements',
    severity: 'high',
    regulationCode: '11-700-2',
    regulationName: 'PCA Qualifications',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your personal care aides meet Hawaii\'s qualification requirements?',
        inputType: 'checklist',
        options: [
          { value: 'nurse_aide', label: 'Completed basic nurse aide course in state-approved program, OR' },
          { value: 'equivalent', label: 'Completed equivalent course approved by DOH, OR' },
          { value: 'licensed', label: 'Hold professional license (CNA, LPN, RN, etc.)' },
        ],
        required: true,
        helpText: 'HAR 11-700-2 defines "personal care aide" qualifications',
      },
      {
        id: 'q2',
        question: 'Do homemakers performing only homemaker services meet the homemaker definition?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have experience or training in homemaker services' },
          { value: 'most', label: 'Most meet requirements' },
          { value: 'na', label: 'Not applicable - no homemaker-only staff' },
          { value: 'no', label: 'No / not verified' },
        ],
        required: true,
        helpText: '"Homemaker" must have experience or training in homemaker services per HAR 11-700-2',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 1 },
        { condition: 'includes', questionId: 'q2', value: ['all', 'na'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'countLessThan', questionId: 'q1', value: 1 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff qualifications meet HAR 11-700 requirements.',
      atRisk: 'Some staff may not meet qualification requirements. Verify all personal care aides have completed required training.',
      nonCompliant: 'Staff are working who do not meet Hawaii qualification requirements. This violates HAR 11-700.',
      unknown: 'Verify all staff qualifications against HAR 11-700 definitions.',
    },
    stateNotes: [
      'Personal care aide: Completed basic nurse aide course OR equivalent OR licensed professional',
      'Homemaker: Experience or training in homemaker services',
      'Supervisor: 2 years experience OR professional license (RN, LPN, SW, PT, OT)',
      'Administrator cannot allow staff to perform services outside their scope (HAR 11-700-9(b))',
    ],
  },

  {
    id: 'HI-E3',
    itemId: 'E3',
    stateCode: 'HI',
    title: 'Home Health Aide Training (HAR 11-97.1 / 42 CFR 484)',
    description: 'Medicare-certified Home Health Agency aide training requirements',
    severity: 'high',
    regulationCode: '11-97.1',
    regulationName: 'HHA Training',
    agencyName: 'DOH-OHCA Medicare Section',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency a Medicare-certified Home Health Agency?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, Medicare-certified' },
          { value: 'no', label: 'No, not Medicare-certified / Home Care Agency only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL your home health aides have at least 75 hours of training AND passed a competency evaluation?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs meet 75-hour requirement with competency eval' },
          { value: 'most', label: 'Most meet requirements' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: '42 CFR 484.80 requires 75 hours training for Medicare HHAs',
      },
      {
        id: 'q3',
        question: 'Do home health aides receive at least 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs receive 12+ hours annually' },
          { value: 'some', label: 'Some receive required training' },
          { value: 'no', label: 'No annual in-service program' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
        { condition: 'equals', questionId: 'q3', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['most'], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Home health aide training meets requirements.',
      atRisk: 'Some home health aides may not meet training requirements. Verify all HHAs have 75 hours training plus competency evaluation.',
      nonCompliant: 'Home health aides do not meet 42 CFR 484.80 training requirements. This jeopardizes Medicare certification.',
      unknown: 'Verify HHA training against 42 CFR 484.80 requirements.',
    },
    stateNotes: [
      'Medicare HHAs: 75 hours training required (42 CFR 484.80)',
      'Competency evaluation required (practical skills test)',
      '12 hours annual in-service training required',
      'RN must supervise HHAs per 42 CFR 484.80(h)',
      'Non-Medicare Home Care Agencies follow HAR 11-700 (personal care aide requirements)',
    ],
  },

  {
    id: 'HI-E4',
    itemId: 'E4',
    stateCode: 'HI',
    title: 'Orientation and In-Service Training',
    description: 'Staff orientation and ongoing training requirements',
    severity: 'medium',
    regulationCode: '11-700-8(6)',
    regulationName: 'Orientation',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide orientation training on agency policies and procedures to all new personnel?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, documented orientation for all new staff' },
          { value: 'no', label: 'No formal orientation program' },
        ],
        required: true,
        helpText: 'HAR 11-700-8(6) requires orientation program for all personnel',
      },
      {
        id: 'q2',
        question: 'Do you provide continuous in-service education?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular documented in-service training' },
          { value: 'occasional', label: 'Occasional training, not consistent' },
          { value: 'no', label: 'No in-service program' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Does orientation/training include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Agency policies and procedures' },
          { value: 'service_plan', label: 'Following the service plan' },
          { value: 'infection', label: 'Infection control' },
          { value: 'safety', label: 'Safety and emergency procedures' },
          { value: 'abuse', label: 'Abuse recognition and mandatory reporting' },
          { value: 'hipaa', label: 'HIPAA and confidentiality' },
          { value: 'emergency', label: 'Hawaii-specific emergency preparedness (hurricane, tsunami, volcanic)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'regular' },
        { condition: 'countAtLeast', questionId: 'q3', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'occasional' },
        { condition: 'countAtLeast', questionId: 'q3', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation and in-service training program meets Hawaii requirements.',
      atRisk: 'Training program is inconsistent or incomplete. Ensure documented orientation and continuous in-service education.',
      nonCompliant: 'No formal orientation or training program. This violates HAR 11-700-8(6) / HAR 11-97.1-5(k).',
      unknown: 'Review training program against Hawaii requirements.',
    },
    stateNotes: [
      'HAR 11-700-8(6): Orientation AND continuous in-service education required',
      'HAR 11-97.1-5(k): Planned program of orientation and continuous in-service',
      'Include Hawaii-specific emergency preparedness (hurricane, tsunami, volcanic activity)',
      'Training must be documented in personnel files',
    ],
  },

  {
    id: 'HI-E5',
    itemId: 'E5',
    stateCode: 'HI',
    title: 'Professional License Verification (DCCA PVL)',
    description: 'Hawaii requires verification of all professional licenses through DCCA',
    severity: 'high',
    regulationCode: '457',
    regulationName: 'License Verification',
    agencyName: 'Department of Commerce and Consumer Affairs - Professional and Vocational Licensing',
    agencyAbbr: 'DCCA-PVL',
    agencyPhone: '(808) 586-3000',
    agencyUrl: 'https://cca.hawaii.gov/pvl/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your licensed professionals (nurses, therapists) have current, active Hawaii licenses?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all licenses verified and current' },
          { value: 'most', label: 'Most are verified, some pending' },
          { value: 'na', label: 'Not applicable - no licensed professionals on staff' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you verify licenses through DCCA PVL online system BEFORE hire and track renewals?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'hire_only' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All professional licenses verified through DCCA PVL with ongoing tracking.',
      atRisk: 'License verification or tracking is inconsistent. Verify all licenses and implement renewal tracking.',
      nonCompliant: 'Licensed professionals working without verified Hawaii licenses violates HRS Chapter 457. Verify all licenses immediately.',
      unknown: 'Verify all professional licenses with DCCA PVL at pvl.ehawaii.gov.',
    },
    stateNotes: [
      'DCCA PVL: Free online license verification at pvl.ehawaii.gov',
      'RN/LPN licenses renew every 2 years (even-numbered years)',
      'Hawaii participates in Nurse Licensure Compact (NLC) as of 2024',
      'Compact nurses can practice in Hawaii with multistate license from compact state',
    ],
  },

  // ============================================
  // SECTION F: SERVICE DELIVERY
  // ============================================
  {
    id: 'HI-F1',
    itemId: 'F1',
    stateCode: 'HI',
    title: 'Service Plan Requirements (HAR 11-700-7)',
    description: 'Home Care Agencies must develop and maintain service plans for each client',
    severity: 'high',
    regulationCode: '11-700-7',
    regulationName: 'Service Plans',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written service plan for EVERY client before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, developed with client input before first service' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal service plan process' },
        ],
        required: true,
        helpText: 'HAR 11-700-7(a) requires supervisor to develop service plan with client/representative',
      },
      {
        id: 'q2',
        question: 'Do service plans include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'client_input', label: 'Developed with input from client/representative' },
          { value: 'capabilities', label: 'Recognition of client\'s capabilities and choices' },
          { value: 'responsibilities', label: 'Division of responsibility for services' },
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'When and how often services will be provided' },
          { value: 'signatures', label: 'Signed by supervisor AND client/representative' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'How often are service plans reviewed?',
        inputType: 'select',
        options: [
          { value: 'annual_and_change', label: 'At least annually AND when client condition changes' },
          { value: 'annual', label: 'Annually only' },
          { value: 'change_only', label: 'Only when condition changes' },
          { value: 'never', label: 'No formal review schedule' },
        ],
        required: true,
        helpText: 'HAR 11-700-7(b) requires review at least annually and when condition changes',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'annual_and_change' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'includes', questionId: 'q3', value: ['annual', 'change_only'] },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service plan process meets HAR 11-700-7 requirements.',
      atRisk: 'Service plan process has gaps. Ensure plans are developed with client input BEFORE services and reviewed annually.',
      nonCompliant: 'Services are being provided without proper service plans. This violates HAR 11-700-7. Immediately develop plans for all clients.',
      unknown: 'Review service plan process against HAR 11-700-7 requirements.',
    },
    stateNotes: [
      'HAR 11-700-7(a): Service plan developed BY SUPERVISOR with client/representative',
      'Plan must be signed by supervisor AND client/representative',
      'Plan incorporated into client\'s record',
      'Review at least ANNUALLY and whenever condition changes (HAR 11-700-7(b))',
      'Staff must bring condition changes to supervisor\'s attention (HAR 11-700-7(c))',
    ],
  },

  {
    id: 'HI-F2',
    itemId: 'F2',
    stateCode: 'HI',
    title: 'Plan of Treatment - Home Health Agencies (HAR 11-97.1)',
    description: 'Home Health Agencies must have physician-ordered plans of treatment',
    severity: 'high',
    regulationCode: '11-97.1-5(i)',
    regulationName: 'Plan of Treatment',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide skilled home health services (HHA license)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, licensed Home Health Agency' },
          { value: 'no', label: 'No, Home Care Agency only (skip this question)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL patients have a physician-signed plan of treatment before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, some delays in obtaining signatures' },
          { value: 'no', label: 'No formal process' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'How often is the plan of treatment reviewed by the physician?',
        inputType: 'select',
        options: [
          { value: '60days', label: 'At least every 60 days as required' },
          { value: '90days', label: 'Every 90 days' },
          { value: 'less', label: 'Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'HAR 11-97.1-5(i)(3) requires review not less than once every two months',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: '60days' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of treatment process meets HAR 11-97.1 and federal requirements.',
      atRisk: 'Plan of treatment process has delays. Ensure physician signature BEFORE services and review every 60 days.',
      nonCompliant: 'Skilled services without physician-signed plans of treatment violates HAR 11-97.1-5(i). This jeopardizes Medicare certification.',
      unknown: 'Review plan of treatment process against HAR 11-97.1 requirements.',
    },
    stateNotes: [
      'HAR 11-97.1-5(i): Plan of treatment required before services',
      'Physician must sign original plan (HAR 11-97.1-5(i)(2))',
      'Review at least every 2 months/60 days (HAR 11-97.1-5(i)(3))',
      'Medicare: 42 CFR 484.60 requires comprehensive assessment and plan',
      'Only attending physician can terminate services (HAR 11-97.1-5(i)(5))',
    ],
  },

  {
    id: 'HI-F3',
    itemId: 'F3',
    stateCode: 'HI',
    title: 'Supervision Requirements',
    description: 'Supervisor duties and supervisory visit requirements',
    severity: 'high',
    regulationCode: '11-700-9(c)',
    regulationName: 'Supervision',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'For Home Care Agencies: Does the supervisor perform a supervisory visit at each client\'s residence at least once per year?',
        inputType: 'select',
        options: [
          { value: 'annual', label: 'Yes, at least annually for each client' },
          { value: 'less', label: 'Less frequently' },
          { value: 'na', label: 'Not applicable (Home Health Agency only)' },
        ],
        required: true,
        helpText: 'HAR 11-700-9(c) requires supervisory visit at least once a year',
      },
      {
        id: 'q2',
        question: 'For Medicare Home Health Agencies: Does RN supervision of home health aides occur per federal requirements?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'Every 14 days or more frequently' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'less', label: 'Less frequently' },
          { value: 'na', label: 'Not applicable (Home Care Agency or no HHAs)' },
        ],
        helpText: 'Medicare requires RN supervision of HHAs every 14 days',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['annual', 'na'] },
        { condition: 'includes', questionId: 'q2', value: ['every_14', 'na'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'less' },
        { condition: 'equals', questionId: 'q2', value: 'every_30' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'less', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervision practices meet Hawaii requirements.',
      atRisk: 'Supervision frequency may not meet requirements. Review HAR 11-700-9(c) for Home Care and federal requirements for Home Health.',
      nonCompliant: 'Supervision is inadequate. This violates HAR requirements and jeopardizes Medicare certification.',
      unknown: 'Verify supervision practices against applicable requirements.',
    },
    stateNotes: [
      'Home Care Agency supervisor: 2 years experience OR professional license',
      'Supervisor cannot be client\'s case manager (HAR 11-700-2)',
      'Home Care: Annual supervisory visit at client residence required',
      'Medicare HHA: RN supervision every 14 days per 42 CFR 484.80(h)',
      'Consider inter-island logistics when scheduling supervision',
    ],
  },

  // ============================================
  // SECTION G: POLICIES & PROCEDURES
  // ============================================
  {
    id: 'HI-G1',
    itemId: 'G1',
    stateCode: 'HI',
    title: 'Written Policies and Procedures',
    description: 'Required written policies for home care operations',
    severity: 'high',
    regulationCode: '11-700-8',
    regulationName: 'Policies',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'scope', label: 'Scope of services, conditions, and geographic coverage' },
          { value: 'nondiscrimination', label: 'Non-discrimination policy (race, sex, religion, etc.)' },
          { value: 'service_plan', label: 'Service plan development and review procedures' },
          { value: 'client_records', label: 'Client record contents and maintenance' },
          { value: 'orientation', label: 'Orientation program for personnel' },
          { value: 'inservice', label: 'Continuous in-service education program' },
          { value: 'background', label: 'Background check procedures' },
          { value: 'abuse_reporting', label: 'Abuse reporting procedures (APS)' },
          { value: 'emergency', label: 'Emergency and disaster preparedness' },
          { value: 'infection', label: 'Infection control' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Hawaii requirements.',
      atRisk: 'Your policy manual is missing required sections. Review HAR 11-700-8 requirements.',
      nonCompliant: 'Multiple required policies are missing. DOH surveyors will cite incomplete policies. Immediately develop missing policies.',
      unknown: 'Review your policies against HAR requirements.',
    },
    stateNotes: [
      'HAR 11-700-8 specifies required policies for Home Care Agencies',
      'HAR 11-97.1-5 specifies required policies for Home Health Agencies',
      'Non-discrimination policy must cover: race, sex, gender identity, sexual orientation, color, religion, ethnicity, country of origin, marital status',
      'Policies must be available for DOH inspection',
    ],
  },

  {
    id: 'HI-G2',
    itemId: 'G2',
    stateCode: 'HI',
    title: 'Emergency Preparedness - Hawaii-Specific Hazards',
    description: 'Hawaii agencies must plan for unique natural disasters',
    severity: 'high',
    regulationCode: '11-700-8',
    regulationName: 'Emergency Preparedness',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your emergency plan address Hawaii-specific hazards? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hurricane', label: 'Hurricane preparedness and evacuation' },
          { value: 'tsunami', label: 'Tsunami warning response and evacuation zones' },
          { value: 'volcanic', label: 'Volcanic activity response (vog, lava, ash)' },
          { value: 'earthquake', label: 'Earthquake procedures' },
          { value: 'flood', label: 'Flash flood response' },
          { value: 'communication', label: 'Communication backup plans (inter-island coordination)' },
          { value: 'priority', label: 'Client priority lists for evacuation' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive and addresses Hawaii-specific hazards.',
      atRisk: 'Emergency plan is incomplete. Include procedures for hurricanes, tsunamis, volcanic activity, and inter-island communication.',
      nonCompliant: 'No emergency preparedness plan in place. Hawaii agencies MUST plan for unique natural disasters.',
      unknown: 'Review your emergency preparedness procedures for Hawaii-specific hazards.',
    },
    stateNotes: [
      'Hawaii faces unique hazards: hurricanes, tsunamis, volcanic activity, earthquakes',
      'Know your tsunami evacuation zone (especially Oahu, Big Island, Maui)',
      'Vog (volcanic smog) considerations for respiratory-compromised clients',
      'Plan for inter-island communication disruptions',
      'Register with Hawaii Emergency Management Agency (HI-EMA) alerts',
      'Medicare HHAs: 42 CFR 484.102 emergency preparedness requirements',
    ],
  },

  // ============================================
  // SECTION H: RECORD KEEPING
  // ============================================
  {
    id: 'HI-H1',
    itemId: 'H1',
    stateCode: 'HI',
    title: 'Client Record Requirements',
    description: 'Documentation and maintenance of client records',
    severity: 'high',
    regulationCode: '11-700-8(5)',
    regulationName: 'Client Records',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain a separate record for each client?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'HAR 11-700-8(5)(A) requires a record for each client',
      },
      {
        id: 'q2',
        question: 'Do staff document information about services rendered in the client record?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, documented after each visit/service' },
          { value: 'usually', label: 'Usually, some gaps exist' },
          { value: 'no', label: 'No consistent documentation' },
        ],
        required: true,
        helpText: 'HAR 11-700-8(5)(B) requires staff to document services rendered',
      },
      {
        id: 'q3',
        question: 'Are client records maintained confidentially?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, available only to authorized personnel' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'HAR 11-700-9(d)(6) requires confidentiality of client records',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'usually', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client record practices meet Hawaii requirements.',
      atRisk: 'Documentation practices have gaps. Ensure services are documented after each visit.',
      nonCompliant: 'Client record practices are inadequate. This violates HAR requirements.',
      unknown: 'Review client record practices against HAR requirements.',
    },
    stateNotes: [
      'Separate record for each client required',
      'Staff must document services rendered',
      'Records must be confidential - available only to authorized personnel as required by law',
      'Home Health Agencies: clinical records per HAR 11-97.1-5(h)',
    ],
  },

  {
    id: 'HI-H2',
    itemId: 'H2',
    stateCode: 'HI',
    title: 'Record Retention',
    description: 'How long records must be maintained',
    severity: 'medium',
    regulationCode: '11-700-9(d)(5)',
    regulationName: 'Record Retention',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client and employee records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Medicare requires 5 years minimum per 42 CFR 484.110; Hawaii recommends 7 years',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Hawaii recommendations.',
      atRisk: 'Consider extending retention to 7 years for best practice. Medicare requires minimum 5 years.',
      nonCompliant: 'Your record retention does not meet minimum requirements. Immediately update your retention policy.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Medicare: 5 years minimum per 42 CFR 484.110',
      'Hawaii recommends 7 years for consistency with other facility types',
      'Records for minors require longer retention',
      'Both client and employee records subject to requirement',
    ],
  },

  // ============================================
  // SECTION I: INSURANCE & COMPLIANCE
  // ============================================
  {
    id: 'HI-I1',
    itemId: 'I1',
    stateCode: 'HI',
    title: 'Insurance Requirements',
    description: 'Liability and workers\' compensation insurance requirements',
    severity: 'high',
    regulationCode: '386',
    regulationName: 'Insurance',
    agencyName: 'Hawaii Department of Labor - Disability Compensation Division',
    agencyAbbr: 'DLIR-DCD',
    agencyPhone: '(808) 586-9161',
    agencyUrl: 'https://labor.hawaii.gov/dcd/',
    questions: [
      {
        id: 'q1',
        question: 'What is your general liability insurance per-occurrence coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'Recommended minimum: $1,000,000 per occurrence',
      },
      {
        id: 'q2',
        question: 'What is your general liability insurance aggregate coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'Recommended minimum: $3,000,000 aggregate',
      },
      {
        id: 'q3',
        question: 'Do you maintain professional liability/malpractice insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required for skilled nursing services',
      },
      {
        id: 'q4',
        question: 'Do you maintain workers\' compensation insurance covering all employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Workers\' compensation is REQUIRED in Hawaii per HRS Chapter 386',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 1000000 },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 3000000 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 500000 },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage meets Hawaii requirements and industry standards.',
      atRisk: 'Your insurance may be below recommended levels. Review coverage with your insurance provider.',
      nonCompliant: 'Missing workers\' compensation insurance violates HRS Chapter 386. This is a criminal offense in Hawaii.',
      unknown: 'Verify your insurance meets Hawaii requirements.',
    },
    stateNotes: [
      'Workers\' compensation REQUIRED per HRS Chapter 386 - NO exceptions',
      'Failure to maintain WC is a criminal offense',
      'Professional liability required for skilled nursing services',
      'Proof of insurance typically required with license application',
      'DLIR-DCD: (808) 586-9161',
    ],
  },

  {
    id: 'HI-I2',
    itemId: 'I2',
    stateCode: 'HI',
    title: 'Quality Assurance Program',
    description: 'Ongoing quality assessment and performance improvement',
    severity: 'medium',
    regulationCode: '11-97.1-5(n)',
    regulationName: 'Quality Assurance',
    agencyName: 'DOH-OHCA',
    agencyAbbr: 'DOH-OHCA',
    agencyPhone: '(808) 586-4400',
    agencyUrl: 'https://health.hawaii.gov/ohca/',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct systematic evaluation of your program?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular documented evaluations (at least every 2 years)' },
          { value: 'occasional', label: 'Occasional reviews, not consistently documented' },
          { value: 'no', label: 'No quality evaluation program' },
        ],
        required: true,
        helpText: 'HAR 11-97.1-5(n) requires evaluation at least every 2 years for HHAs',
      },
      {
        id: 'q2',
        question: 'Does your evaluation include review of patient records to determine services are used appropriately?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'regular' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'occasional', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance program meets requirements.',
      atRisk: 'Quality evaluations are not conducted consistently. Implement regular documented reviews.',
      nonCompliant: 'No quality evaluation program exists. This violates HAR requirements.',
      unknown: 'Review quality assurance requirements.',
    },
    stateNotes: [
      'HAR 11-97.1-5(n): Systematic evaluation at least every 2 years',
      'Include sample-based review of patient records',
      'Evaluate if patient needs are being met qualitatively and quantitatively',
      'Medicare HHAs: QAPI program required per 42 CFR 484.65',
    ],
  },
];

export default HAWAII_QUESTIONS;